
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface ProfileData {
  company_name: string;
  company_address: string | null;
  company_country: string | null;
}

export const useProfileSettings = () => {
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData>({
    company_name: "",
    company_address: null,
    company_country: null,
  });

  const handleSettingsClick = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to edit your profile",
          variant: "destructive",
        });
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('company_name, company_address, company_country')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (profile) {
        setProfileData(profile);
        setShowProfileDialog(true);
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/login", { replace: true });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to sign out",
        variant: "destructive",
      });
    }
  };

  return {
    showProfileDialog,
    setShowProfileDialog,
    showSubscriptionDialog,
    setShowSubscriptionDialog,
    showDeleteAccountDialog,
    setShowDeleteAccountDialog,
    profileData,
    handleSettingsClick,
    handleSignOut,
  };
};
